import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Button, Skeleton, Typography, Grid, Alert } from "antd";
import {
  loadListOfCategories,
  loadListOfSubCategories,
  getDashboardFilters,
} from "@/common/document-filters/document-filters-reducer";
import { hidePanel } from "@/pages/shared/shared.reducer";
import { openErrorNotification } from "@/notification";
import FloatInput from "@/common/float-input";
import get from "lodash.get";
import { haveAbilityToChangeCategory } from "@/pages/login/permission-service";
import { forbidden_chars } from "@/constants/constant.string";
import { containForbiddenChars } from "@/constants/methods";
import { useFormContext } from "@/common/form-context";
import { dashboardApi } from "@/pages/dashboard/dashboard.reducer";
import { runSiteQuery } from "@/common/site-details/site.query";
import {
  fixedCategoryKey,
  getFixedCategoryOptions,
  getFixedSubCategoryOptions,
} from "@/common/site-details/fixed-category-options";
import { controlledCategoryNotice as fixedCategoryNote } from "@/common/site-details/controlled-category-notice";

const { Paragraph } = Typography;

const NO_ITEMS = [];
const NO_PERMISSIONS = [];

const UpdateCategoriesPanel = ({
  tableId,
  selectedItems = [],
  refetch,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const dispatch = useDispatch();
  const siteId = useSelector((state) =>
    parseInt(get(state.shared, "siteId", null))
  );
  const categoryList = useSelector((state) =>
    get(state.documentFilters, "allCategories", NO_ITEMS)
  );
  const subCategoryList = useSelector((state) =>
    get(state.documentFilters, "allSubCategories", NO_ITEMS)
  );
  const permissions = useSelector((state) =>
    get(state.userDetails, "permissions", NO_PERMISSIONS)
  );
  const sites = useSelector((state) =>
    get(state.dashboardPage, "sites", NO_ITEMS)
  );
  const filters = useSelector(
    (state) => get(state.shared, "dashboardTable.filters") || NO_ITEMS
  );
  const { form, markFieldAsTouched, isAnyFieldTouched, onFinish } =
    useFormContext();
  const selectedCategory = Form.useWatch("category", form);
  const selectedSubCategory = Form.useWatch("subCategory", form);
  const [fixedCategoryTree, setFixedCategoryTree] = useState(null);

  const categoryLoading = useSelector(
    (state) => state.dashboardPage.categoryLoading
  );
  const fixedCategoriesEnabled = useMemo(
    () => sites.some(item => item.siteId == siteId && item.fixedCategoriesEnabled),
    [sites, siteId]
  );

  const exitPanel = () => {
    dispatch(hidePanel("changeCategoriesPanel"));
  };

  useEffect(() => {
    dispatch(loadListOfCategories(siteId, null, 0));
    dispatch(loadListOfSubCategories(siteId, null, 0));
  }, [dispatch, siteId]);

  useEffect(() => {
    let cancelled = false;
    if (!fixedCategoriesEnabled || !siteId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- fetches the fixed-category tree for the current site; the reset must happen alongside the fetch
      setFixedCategoryTree(null);
      return undefined;
    }

    runSiteQuery(dispatch, "getFixedCategories", siteId).then((data) => {
      if (!cancelled) {
        setFixedCategoryTree(data);
      }
    }).catch(() => {
      if (!cancelled) {
        setFixedCategoryTree(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, fixedCategoriesEnabled, siteId]);

  const handleCategoryUpdate = (e) => {
    const { category, subCategory } = e;
    const itemsToUpdate = haveAbilityToChangeCategory(
      permissions,
      selectedItems
    );

    if (itemsToUpdate && itemsToUpdate.length > 0) {
      dispatch(
        dashboardApi.endpoints.updateCategories.initiate({
          category,
          subCategory,
          documents: itemsToUpdate,
          panelId: "changeCategoriesPanel",
          refetch,
        })
      ).then(() => {
        dispatch(getDashboardFilters(filters, tableId, true));
      });
    } else {
      const errorMessage =
        !itemsToUpdate || itemsToUpdate.length === 0
          ? "No selected documents"
          : "Invalid fields";
      openErrorNotification(errorMessage);
    }
    form.setFieldsValue({ category: "", subCategory: "" });
  };

  const validateCategory = (_, value) => {
    if (value && containForbiddenChars(value)) {
      return Promise.reject(
        new Error(`Category/Sub Category can't include ${forbidden_chars}`)
      );
    }
    return Promise.resolve();
  };

  const hasFixedOption = (options, value) =>
    !fixedCategoryKey(value) || options.some(
      (option) => fixedCategoryKey(option.value) === fixedCategoryKey(value)
    );

  const handleCategoryChange = (value) => {
    markFieldAsTouched("category");

    if (
      fixedCategoriesEnabled &&
      fixedCategoryTree &&
      fixedCategoryKey(value) &&
      hasFixedOption(getFixedCategoryOptions(fixedCategoryTree), value) &&
      !hasFixedOption(
        getFixedSubCategoryOptions(fixedCategoryTree, value),
        selectedSubCategory
      )
    ) {
      form.setFieldsValue({ subCategory: undefined });
    }
  };

  const handleSubCategoryChange = (value) => {
    markFieldAsTouched("subCategory");

    if (
      fixedCategoriesEnabled &&
      fixedCategoryTree &&
      fixedCategoryKey(value) &&
      hasFixedOption(getFixedSubCategoryOptions(fixedCategoryTree), value) &&
      !hasFixedOption(
        getFixedCategoryOptions(fixedCategoryTree, value),
        selectedCategory
      )
    ) {
      form.setFieldsValue({ category: undefined });
    }
  };

  const categorySuggestions = useMemo(
    () =>
      fixedCategoriesEnabled && fixedCategoryTree
        ? getFixedCategoryOptions(fixedCategoryTree, selectedSubCategory)
        : Object.entries(categoryList).map(([key, value]) => ({
        key,
        label: value,
        value,
      })),
    [categoryList, fixedCategoriesEnabled, fixedCategoryTree, selectedSubCategory]
  );

  const subCategorySuggestions = useMemo(
    () =>
      fixedCategoriesEnabled && fixedCategoryTree
        ? getFixedSubCategoryOptions(fixedCategoryTree, selectedCategory)
        : Object.entries(subCategoryList).map(([key, value]) => ({
        key,
        label: value,
        value,
      })),
    [subCategoryList, fixedCategoriesEnabled, fixedCategoryTree, selectedCategory]
  );

  const renderItemParagraphs = () =>
    selectedItems.map((item, index) => (
      <Paragraph
        key={index}
        ellipsis={{ rows: 5, expandable: true, symbol: "more" }}
      >
        <div>
          <span className="body-large-roman" style={{ color: "var(--dms-color-text-muted)" }}>
            ●&nbsp;
          </span>
          <span style={{ color: "var(--dms-color-text)" }}>
            <small>
              {item.category}/{item.subCategory}/
            </small>
            <b>{item.documentNumber}</b>
          </span>
        </div>
      </Paragraph>
    ));

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish(handleCategoryUpdate)}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Select desired categories for both Category and Sub Category before the change can be implemented and saved. This tool can be used to change the categories for many documents/drawings at once but it will always change them all to the same categories populated below. For granular bulk changes, use the Bulk Metadata Editor. Individual category changes can be made in the Document Detail tab."
        />
        {fixedCategoriesEnabled && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title={fixedCategoryNote}
          />
        )}
        {categoryLoading ? (
          <>
            <Skeleton.Input active />
            <Skeleton.Input active />
          </>
        ) : (
          <>
            <Form.Item
              name="category"
              rules={[
                { required: true, message: "Category is required" },
                { validator: validateCategory }
              ]}
            >
              <FloatInput
                forceUppercase
                autoComplete
                label="Category"
                id="category"
                suggestions={categorySuggestions}
                allowCustom={!fixedCategoriesEnabled}
                onChange={handleCategoryChange}
              />
            </Form.Item>
            <Form.Item
              name="subCategory"
              rules={[
                { required: true, message: "Sub Category is required" },
                { validator: validateCategory }
              ]}
              className="mt-3"
            >
              <FloatInput
                forceUppercase
                autoComplete
                label="Sub Category"
                id="subCategory"
                suggestions={subCategorySuggestions}
                allowCustom={!fixedCategoriesEnabled}
                onChange={handleSubCategoryChange}
              />
            </Form.Item>
          </>
        )}
        <div className="body-large-roman" style={{ color: "var(--dms-color-text-muted)" }}>
          Document / drawing #:
        </div>
        <div style={{ gap: 6, marginBottom: 26 }}>{renderItemParagraphs()}</div>
      </Form>
      <div className="footer document-detail-footer"
        style={ isMobile ? {
          display: "grid",
          rowGap: "10px",
          width: "100%",
          minHeight: "22vh",
          alignContent: "flex-start"
        } : {
            display: "flex",
            paddingRight: "45px"
        }}>
        <Button
          type="primary"
          onClick={() => {
            form.submit();
          }}
          disabled={!isAnyFieldTouched()}
        >
          <i className="ri-save-line" />
          Save and Close
        </Button>
        <Button className="common-button" onClick={exitPanel}>
          <i className="ri-close-line" />
          Cancel
        </Button>
      </div>
    </>
  );
};

export default UpdateCategoriesPanel;
